/**
 * «Mitt arbeid» — the work hub on the dashboard.  MKK adoption Batch 1 (MKK_ADOPTION_BATCH_1.md §1.4, §7).
 *
 * Drawn only when the mosque has one of `tasks`, `checklists` or `planner` and the person may use it; otherwise it is
 * not there at all (not an empty card, and no request is made). Counts first, each a link to the list it counts, then
 * at most five lines, most urgent first, then this week in the planner on one line. A green state says so once, in the
 * dashboard's own words. The numbers are core's (work-hub.attention), computed by the server for THIS person.
 */
import React from 'react';
import { can, type Body } from '../lib/api';
import { useLoad } from '../lib/hooks';
import { useNav } from '../lib/nav';
import { shortDate } from '../lib/format';
import { Card, Tag } from './ui';

const WORK_CAPS = ['tasks', 'checklists', 'planner'];

/** Whether this person has a work hub here at all: the mosque has one of its capabilities, and they may use it. */
export function useWorkHub() {
  const { caps } = useNav();
  return Boolean(caps && caps.some((c) => WORK_CAPS.includes(c)))
    // Literal can('…') calls on purpose: core's access test enumerates them from the source and checks each name exists.
    && (can('work.assigned') || can('planner.read') || can('tasks.manage') || can('checklists.manage'));
}

export function WorkCard() {
  const has = useWorkHub();
  const { data } = useLoad<Body>(has ? '/work/summary' : null);
  if (!has || !data) return null;
  const c: Body = data.counts || {};
  const to = (hash: string) => () => { location.hash = hash; };
  const chips: [number | undefined, string, string][] = [
    [c.myOverdue, 'forfalt hos deg', '/oppgaver?due=overdue'],
    [c.myToday, 'i dag hos deg', '/oppgaver?due=today'],
    [c.myChecklistItems, c.myChecklistItems === 1 ? 'sjekkpunkt til deg' : 'sjekkpunkter til deg', '/sjekklister'],
    [c.overdue, 'forfalt i alt', '/oppgaver?scope=all&due=overdue'],
    [c.unassignedHigh, 'høy prioritet uten ansvarlig', '/oppgaver?scope=all&assignee=none'],
    [c.inactiveAssignee, 'hos deaktivert ansatt', '/oppgaver?scope=all'],
    [c.checklistsSoon, c.checklistsSoon === 1 ? 'sjekkliste snart' : 'sjekklister snart', '/sjekklister?tab=active'],
  ];
  const shown = chips.filter(([n]) => typeof n === 'number' && n > 0);
  const list: Body[] = data.list || [];
  const week: Body[] = data.week || [];
  return (
    <Card title="Mitt arbeid" className="work-card">
      <div className="card-body">
        {shown.length > 0 && (
          <ul className="work-chips" aria-label="Det som venter">
            {shown.map(([n, label, hash]) => (
              <li key={label}><a href={'#' + hash} onClick={to(hash)}><b>{n}</b> {label}</a></li>
            ))}
          </ul>
        )}
        {list.length > 0 && (
          <ul className="work-rows compact">{list.map((x) => (
            <li key={x.kind + x.id}>
              <a className="work-row" href={x.kind === 'task' ? `#/oppgaver?scope=${can('tasks.manage') ? 'all' : 'mine'}&id=${x.id}` : `#/sjekklister?tab=active&id=${x.id}`}>
                <span className="work-row-title">{x.kind === 'checklist' ? 'Sjekkliste: ' : ''}{x.title}</span>
                <span className="work-row-meta">
                  {x.state === 'overdue' ? <Tag tone="red">Forfalt</Tag> : x.state === 'today' ? <Tag tone="gold">I dag</Tag>
                    : x.state === 'soon' ? <Tag tone="gold">Snart</Tag> : <Tag tone="grey">Uten ansvarlig</Tag>}
                  {x.dueOn && <time dateTime={x.dueOn}>{shortDate(x.dueOn)}</time>}
                  {x.kind === 'checklist' && <span>{x.done} av {x.total}</span>}
                </span>
              </a>
            </li>
          ))}</ul>
        )}
        {data.more > 0 && <p className="note">Og {data.more} til.</p>}
        {week.length > 0 && (
          <p className="work-week"><a href="#/arshjul" onClick={to('/arshjul')}>Denne uken</a>: {week.map((w, i) => (
            <span key={w.id + w.date}>{i ? ' · ' : ''}{w.title} <time dateTime={w.date}>{shortDate(w.date)}</time>{w.startTime ? ' ' + w.startTime : ''}</span>
          ))}</p>
        )}
        {data.allClear_no && shown.length === 0 && list.length === 0 && <p className="work-clear">{data.allClear_no}</p>}
      </div>
    </Card>
  );
}
